import axios from "axios";
import { userBelongsToUserGroup } from "./AuthorizationUtil";

// ─── User Permission Utilities ────────────────────────────────────────────────

// Checks if user can modify any entry (admin, owner, co-owner, author, or has modify permission)
export function userCanModifyEntry(userIsAdmin, selectedTemplate, userEmail, entryAuthor) {
  if (userIsAdmin) return true;
  if (selectedTemplate?.owner.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(userEmail, co.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (entryAuthor && entryAuthor.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.sharedPermission?.some((perm) => {
      if (
        !perm.isUserGroup &&
        perm.entity.toLowerCase() === userEmail.toLowerCase() &&
        perm.permission === "MakeEntry (Modify All)"
      ) {
        return true;
      }
      if (perm.isUserGroup && perm.userGroupId && perm.permission === "MakeEntry (Modify All)") {
        return userBelongsToUserGroup(userEmail, perm.userGroupId);
      }
      return false;
    })
  )
    return true;
  return false;
}

// Checks if user can create new entries (admin, owner, co-owner, or has create permission)
export function userCanCreateEntry(userIsAdmin, selectedTemplate, userEmail) {
  if (userIsAdmin) return true;
  if (selectedTemplate?.owner.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(userEmail, co.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (
    selectedTemplate?.sharedPermission?.some((sp) => {
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === userEmail.toLowerCase() &&
        sp.permission === "MakeEntry"
      ) {
        return true;
      }
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "MakeEntry") {
        return userBelongsToUserGroup(userEmail, sp.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (
    selectedTemplate?.sharedPermission?.some((sp) => {
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === userEmail.toLowerCase() &&
        sp.permission === "MakeEntry (Modify All)"
      ) {
        return true;
      }
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "MakeEntry (Modify All)") {
        return userBelongsToUserGroup(userEmail, sp.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (
    selectedTemplate?.sharedPermission?.some((sp) => {
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === userEmail.toLowerCase() &&
        sp.permission === "ViewAuthorOnly"
      ) {
        return true;
      }
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "ViewAuthorOnly") {
        return userBelongsToUserGroup(userEmail, sp.userGroupId);
      }
      return false;
    })
  )
    return true;
  return false;
}

// Checks if user can edit an entry (admin, owner, co-owner, author, or has modify permission)
export function userCanEditEntry(userIsAdmin, selectedTemplate, userEmail, entryAuthor) {
  if (userIsAdmin) return true;
  if (selectedTemplate?.owner.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(userEmail, co.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (entryAuthor && entryAuthor.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.sharedPermission?.some((permission) => {
      if (
        !permission.isUserGroup &&
        permission.entity.toLowerCase() === userEmail.toLowerCase() &&
        permission.permission === "MakeEntry (Modify All)"
      ) {
        return true;
      }
      if (
        permission.isUserGroup &&
        permission.userGroupId &&
        permission.permission === "MakeEntry (Modify All)"
      ) {
        return userBelongsToUserGroup(userEmail, permission.userGroupId);
      }
      return false;
    })
  )
    return true;
  return false;
}

// Checks if user can view an entry (admin, owner, co-owner, author, or has view/modify permission)
export function userCanViewEntry(userIsAdmin, selectedTemplate, userEmail, entryAuthor) {
  if (userIsAdmin) return true;
  if (selectedTemplate?.owner.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(userEmail, co.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (entryAuthor && entryAuthor.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.sharedPermission?.some((permission) => {
      if (
        !permission.isUserGroup &&
        permission.entity.toLowerCase() === userEmail.toLowerCase() &&
        permission.permission === "MakeEntry (Modify All)"
      ) {
        return true;
      }
      if (
        permission.isUserGroup &&
        permission.userGroupId &&
        permission.permission === "MakeEntry (Modify All)"
      ) {
        return userBelongsToUserGroup(userEmail, permission.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (
    selectedTemplate?.sharedPermission?.some((permission) => {
      if (
        !permission.isUserGroup &&
        permission.entity.toLowerCase() === userEmail.toLowerCase() &&
        permission.permission === "View"
      ) {
        return true;
      }
      if (permission.isUserGroup && permission.userGroupId && permission.permission === "View") {
        return userBelongsToUserGroup(userEmail, permission.userGroupId);
      }
      return false;
    })
  )
    return true;
  return false;
}

// Checks if user can delete an entry (admin, owner, co-owner, or author only)
export function userCanDeleteEntry(userIsAdmin, selectedTemplate, userEmail, entryAuthor) {
  if (userIsAdmin) return true;
  if (selectedTemplate?.owner.toLowerCase() === userEmail.toLowerCase()) return true;
  if (
    selectedTemplate?.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
        return true;
      }
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(userEmail, co.userGroupId);
      }
      return false;
    })
  )
    return true;
  if (entryAuthor && entryAuthor.toLowerCase() === userEmail.toLowerCase()) return true;
  return false;
}

// ─── Encryption Utilities ─────────────────────────────────────────────────────

// Encrypts an entry ID using AES-GCM for external link tokens
export async function encryptEntryId(entryId) {
  const encoder = new TextEncoder();
  const data = encoder.encode(entryId);

  // Get encryption key from environment or use a default key
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(process.env.REACT_APP_ENCRYPTION_KEY || "default-32-char-encryption-key"),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode("peos-salt"),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, key, data);

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Convert to base64
  return btoa(String.fromCharCode(...combined));
}

// ─── Data Filtering Utilities ─────────────────────────────────────────────────

// Filters entries by user's business unit unless user is admin/owner/co-owner
export function businessUnitFilter(
  data,
  selectedTemplate,
  userIsAdmin,
  userEmail,
  userBusinessUnit
) {
  if (!selectedTemplate) return;
  if (data?.length > 0 && !userIsAdmin) {
    let hasBusinessUnitField = false;
    const filteredData = data.filter((entry) => {
      return entry.sections.some((section, sIndex) => {
        return section.fields.some((field, fIndex) => {
          if (selectedTemplate.owner.toLowerCase() === userEmail.toLowerCase()) return true;
          if (
            selectedTemplate.coOwners?.some(
              (co) => co.user.toLowerCase() === userEmail.toLowerCase()
            )
          )
            return true;
          if (field.fieldType === "BusinessUnit") {
            hasBusinessUnitField = true;
            if (selectedTemplate.sections?.[sIndex]?.fields?.[fIndex]?.properties?.FilterOverride)
              return true;
            return field.value === userBusinessUnit;
          }
          return false;
        });
      });
    });
    const finalData = hasBusinessUnitField ? filteredData : data;
    return finalData;
  } else {
    return data;
  }
}

// ─── Field Value Utilities ────────────────────────────────────────────────────

// Gets the revision count for a specific field from an entry object
export const getRevisionCount = (obj, fieldName) => {
  for (const section of obj.sections) {
    for (const field of section.fields) {
      if (field.fieldName === fieldName) {
        return field.revisionCount ?? 0;
      }
    }
  }
  return "Rev # Not Found";
};

// ─── UI Measurement Utilities ─────────────────────────────────────────────────

// Calculates text width in pixels using canvas for dynamic column sizing
export function getTextWidth(text, font) {
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

// ─── Dataset Utilities ────────────────────────────────────────────────────────

// Finds the dataset ID associated with a field by field name
export function findDatasetId(sections, fieldName) {
  let result;

  sections?.forEach((section) => {
    section?.fields?.forEach((field) => {
      if (field?.fieldName === fieldName) {
        result = field?.properties.Dataset?.id;
      }
    });
  });

  return result;
}

// ─── Entry Update Utilities ───────────────────────────────────────────────────

// Persists entry field changes to the database via API
export const updateDatabase = async (entry, target, userEmail, templateData) => {
  const entryCopy = JSON.tryParse(JSON.stringify(entry));
  entryCopy.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.fieldName === (target.props?.name || target.name)) {
        if (field.fieldType === "Date" || field.fieldType === "DateTime") {
          const newDate = new Date(target.value);
          const timezoneOffset = new Date().getTimezoneOffset() * 60000;
          const convertedDate = new Date(newDate.getTime() - timezoneOffset)
            .toISOString()
            .replace("Z", "");
          field.value = convertedDate;
        } else {
          field.value = target.value ?? "";
        }
      }
      if (field.properties?.["ParentFieldName"]) {
        if (field.properties["ParentFieldName"] === target.props.name) {
          field.value = "";
        }
      }
    });
  });
  try {
    console.log("Submitting entry from grid...", entryCopy);

    await axios.put(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/${
        entryCopy.id
      }?userId=${userEmail}&templateSync=${templateData?.properties?.["TemplateSync"] ?? false}`,
      entryCopy
    );
  } catch (error) {
    console.log("Error response:", error.response);
  }
};

// Clears child field values when parent field changes (cascading update)
export function checkChildren({ fieldName, updatedItem }) {
  updatedItem?.originalItem?.sections?.forEach((section) => {
    section?.fields?.forEach((field) => {
      if (field?.properties?.["ParentFieldName"] === fieldName) {
        field.value = "";
        updatedItem[field?.fieldName] = "";
      }
    });
  });
  return updatedItem;
}

// Updates the grid item state and syncs with original entry data
export function updateOriginalItem(event, fieldName, value, filteredGridData, setFilteredGridData) {
  const itemIndex = event.dataIndex;
  let updatedItem = event.dataItem;
  event.dataItem[fieldName] = value;
  updatedItem = checkChildren({ fieldName: fieldName, updatedItem: event.dataItem });

  updatedItem[fieldName] = value;
  updatedItem.originalItem.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.fieldName === fieldName) {
        field.value = value;
      }
    });
  });
  updatedItem = checkChildren({ fieldName, updatedItem });
  setFilteredGridData((prev) => {
    const updatedData = [...prev];
    updatedData[itemIndex] = updatedItem;
    return updatedData;
  });
}

// ─── Date/Time Utilities ──────────────────────────────────────────────────────

// Re-adds timezone offset to UTC dates for display
export const reAddTimeOffset = (date) => {
  const newDate = new Date(date);
  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
  const convertedDate = new Date(newDate.getTime() + timezoneOffset);
  return convertedDate;
};

// Formats a time value to US locale 12-hour format
export const formatTimeString = (timeString) => {
  const date = new Date(timeString);
  if (!isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  }
  return "";
};

// ─── Numeric Formatting Utilities ─────────────────────────────────────────────

// Formats a numeric value according to the specified format string (e.g., "n2", "c", "p2")
export function formatNumericValue(value, format) {
  if (value === null || value === undefined || value === "") return "";

  const numValue = Number(value);
  if (isNaN(numValue)) return value;

  // Parse format string (e.g., "n2", "c", "p2")
  const formatType = format?.charAt(0) ?? "n";
  const decimals = parseInt(format?.slice(1) || "0", 10);

  const options = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  switch (formatType) {
    case "c":
      options.style = "currency";
      options.currency = "USD";
      break;
    case "p":
      options.style = "percent";
      break;
    default:
      options.style = "decimal";
  }

  return numValue.toLocaleString("en-US", options);
}
